import { useCallback, useEffect, useRef } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from "react";

/**
 * useFocusTrap — initial focus, a scoped Tab cycle, and focus restore, for any
 * overlay that needs them.
 *
 * <b>`Modal` now uses this itself — you usually do not need to.</b> This hook was
 * written when the shared `Modal` (`@/components/ui/Modal`) had no focus
 * management of any kind, as the reversible way to fix one surface at a time
 * without changing the keyboard behaviour of 260 dialogs in a single commit.
 * `Modal` was wired up on 2026-08-02, so a plain dialog is already trapped,
 * focused and restored: pass `initialFocusRef` if you only want a different
 * starting element.
 *
 * <b>Reach for this directly when the dialog's own trap cannot do the job</b> —
 * the live case is a WIZARD, which has to re-run initial focus per step via
 * `focusKey`. Then set `manageFocus={false}` on the `Modal` so it stands down.
 * Two nested traps do not compose: both handle Tab, the inner calls
 * `preventDefault` without `stopPropagation`, and focus moves twice at the cycle
 * boundary.
 *
 * <b>The trap is scoped to the container you attach it to, not to the dialog.</b>
 * `Modal`'s X button lives in its header, OUTSIDE any children you pass it, so a
 * container placed around the body deliberately does NOT include it. Callers must
 * therefore keep a real Cancel control inside the trapped region: closing must
 * never depend on reaching a control the cycle excludes. Escape and the backdrop
 * still work — `Modal` owns those, both through its single `onClose` prop.
 *
 * @example
 * const trap = useFocusTrap<HTMLDivElement>({ active: open, focusKey: step });
 * <Modal open={open} onClose={close} title="…">
 *   <div ref={trap.containerRef} onKeyDown={trap.onKeyDown}>
 *     <Input ref={trap.setInitialFocus} … />
 *     <Button onClick={close}>Cancel</Button>
 *   </div>
 * </Modal>
 */
export interface UseFocusTrapOptions {
  /** Whether the trap is live. Usually the overlay's own `open` flag. */
  active: boolean;
  /**
   * Re-runs initial focus when it changes — pass the current step of a wizard,
   * or a panel key. Focus is NOT restored on a change of this value; only
   * deactivation restores.
   */
  focusKey?: string | number;
}

export interface FocusTrap<T extends HTMLElement = HTMLDivElement> {
  /** Attach to the element whose descendants Tab may reach. */
  containerRef: RefObject<T | null>;
  /**
   * Optional callback ref for the control that should receive focus. A callback
   * (rather than a typed `RefObject`) is what lets the same ref land on an
   * input on one step and a button or an alert `<div>` on the next. When it is
   * unset — or points at a node that has since unmounted — the first focusable
   * descendant of the container is used instead.
   */
  setInitialFocus: (node: HTMLElement | null) => void;
  /** Attach to the same element as `containerRef`. */
  onKeyDown: (ev: ReactKeyboardEvent<HTMLElement>) => void;
}

/**
 * What Tab may reach.
 *
 * <b>Deliberately no visibility filter.</b> jsdom reports no layout, so an
 * `offsetParent`/`getClientRects` test empties this list in every test while
 * looking perfectly correct in a browser — the trap would then silently do
 * nothing under test and pass. Render only the active step inside the container
 * and there is nothing hidden to exclude in the first place.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>({
  active,
  focusKey,
}: UseFocusTrapOptions): FocusTrap<T> {
  const containerRef = useRef<T | null>(null);
  const initialFocusRef = useRef<HTMLElement | null>(null);
  /** Whatever had focus when the trap went live — the control that opened it. */
  const restoreRef = useRef<HTMLElement | null>(null);

  const setInitialFocus = useCallback((node: HTMLElement | null) => {
    initialFocusRef.current = node;
  }, []);

  const focusables = useCallback((): HTMLElement[] => {
    const root = containerRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  }, []);

  // Capture-and-restore, on its own effect keyed ONLY on `active`. Sharing an
  // effect with the focus move below would restore on every `focusKey` change,
  // i.e. yank focus back to the trigger button on every step of a wizard.
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement;
    restoreRef.current = previous instanceof HTMLElement ? previous : null;
    return () => {
      // `isConnected` because the opener is often inside content that has since
      // re-rendered; focusing a detached node throws focus to <body> silently,
      // which is worse than leaving it where it is.
      const target = restoreRef.current;
      restoreRef.current = null;
      if (target && target.isConnected) target.focus();
    };
  }, [active]);

  // Initial focus, re-run per step. Effects run after commit, so the step body
  // is already in the document and any callback ref on it has fired.
  useEffect(() => {
    if (!active) return;
    const explicit = initialFocusRef.current;
    if (explicit && explicit.isConnected) {
      explicit.focus();
      return;
    }
    focusables()[0]?.focus();
  }, [active, focusKey, focusables]);

  const onKeyDown = useCallback(
    (ev: ReactKeyboardEvent<HTMLElement>) => {
      if (ev.key !== "Tab") return;
      const root = containerRef.current;
      if (!root) return;
      const nodes = focusables();
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const activeEl = document.activeElement;
      if (ev.shiftKey) {
        // `!root.contains` covers focus sitting on the container itself (given
        // a tabIndex) or having escaped: either way, wrap to the end.
        if (activeEl === first || !root.contains(activeEl)) {
          ev.preventDefault();
          last.focus();
        }
        return;
      }
      if (activeEl === last) {
        ev.preventDefault();
        first.focus();
      }
    },
    [focusables],
  );

  return { containerRef, setInitialFocus, onKeyDown };
}
